import { serviceNW } from '@/utils/request'

// ===== 固定节点拆分 =====

interface PrologueItem {
  role: string
  content: string
}

interface FixedNodeRequest {
  plot_text: PrologueItem[]
  available_music_types?: string[]
  model?: string
}

interface FixedNodeItem {
  order: number
  plot_text: PrologueItem[]
  image_prompt: string
  music_type: string
  is_new_music_type: boolean
}

export interface FixedNodeResponse {
  nodes: FixedNodeItem[]
  raw_output?: string
}

// ===== AI节点扩展 =====

interface PlotEnding {
  id: number
  description: string
}

interface RoleInfo {
  role_id?: number
  role_name: string
  description: string
}

interface AINodeRequest {
  game_id?: number
  previous_plot?: string
  previous_nodes?: any[]
  target_node_id?: number
  prologue: PrologueItem[]
  task: string
  plot_start: string
  plot_process: string
  plot_endings: PlotEnding[]
  ai_reply_style?: string
  image_requirements?: string
  user_role?: string
  roles?: RoleInfo[]
  attributes?: string[]
  favorabilities?: string[]
  existing_events?: string[]
  existing_easter_eggs?: string[]
  available_music_types?: string[]
  model?: string
}

// AI 扩写【新】：仅依据核心信息
interface AICoreNodeRequest {
  core_info: string
  game_id?: number
  target_node_id?: number
  user_role?: string
  roles?: RoleInfo[]
  attributes?: string[]
  existing_events?: string[]
  existing_easter_eggs?: string[]
  available_music_types?: string[]
  model?: string
}

interface UserMessageBranch {
  trigger: string
  ai_reply: string
  progress_name: string
  progress_gain: number
  examples: string[]
}

interface EasterEgg {
  name: string
  trigger: string
  achievement?: string // 触发该彩蛋后授予的成就名（映射为彩蛋的成就奖励）
}

interface ProgressIndicator {
  name: string
  min_value: number
  max_value: number
  linked_ending: string
  ending_example?: string
  rewards?: {
    event: string
    attributes: { role?: string; name: string; value: number }[] // role=所属角色名（好感度也走属性，属性名为"好感度"）
    favorabilities: { name: string; value: number }[]
  }
}

interface CharacterStatus {
  role: string
  description: string
}

export interface AINodeResponse {
  node_title: string
  image_prompt: string
  music_type: string
  is_new_music_type: boolean
  prologue: PrologueItem[]
  background: string
  character_status: CharacterStatus[]
  current_plot: {
    start: string
    process: string
    plot_endings: PlotEnding[]
  }
  task: string
  user_messages: UserMessageBranch[]
  easter_eggs: EasterEgg[]
  progress_indicators: ProgressIndicator[]
  options?: { progress_name: string; dialogues: { role: string; text: string }[] }[] // AI扩写【新】：预录对话选项
  needs_regeneration: boolean
  scene_roles?: string[]
  ending_roles?: string[] // 结尾参与角色（剧情结尾里出现的角色）
  raw_output?: string
}

// ===== 流式事件类型 =====

export type StreamEvent =
  | { type: 'chunk'; content: string }
  | { type: 'result'; data: any }
  | { type: 'retry'; message: string }
  | { type: 'error'; message: string }

// ===== API 调用函数 =====

export async function expandFixedNodeStream(
  request: FixedNodeRequest,
  onChunk: (text: string) => void,
  onResult: (data: FixedNodeResponse) => void,
  onError: (msg: string) => void
): Promise<void> {
  const response = await serviceNW.post('/api/fixed_node/stream', request, {
    responseType: 'text',
    // 禁用 axios 的 JSON 自动解析
    transformResponse: [(data: string) => data],
  })

  const lines = (response.data as string).split('\n').filter((l: string) => l.trim())
  for (const line of lines) {
    const event: StreamEvent = JSON.parse(line)
    if (event.type === 'chunk') {
      onChunk(event.content)
    } else if (event.type === 'result') {
      onResult(event.data as FixedNodeResponse)
    } else if (event.type === 'error') {
      onError(event.message)
    }
  }
}

export async function expandAINodeStream(
  request: AINodeRequest,
  onChunk: (text: string) => void,
  onResult: (data: AINodeResponse) => void,
  onError: (msg: string) => void
): Promise<void> {
  const response = await serviceNW.post('/api/ai_node/stream', request, {
    responseType: 'text',
    transformResponse: [(data: string) => data],
  })

  const lines = (response.data as string).split('\n').filter((l: string) => l.trim())
  for (const line of lines) {
    const event: StreamEvent = JSON.parse(line)
    if (event.type === 'chunk') {
      onChunk(event.content)
    } else if (event.type === 'result') {
      onResult(event.data as AINodeResponse)
    } else if (event.type === 'retry') {
      onChunk('\n\n' + event.message + '\n\n')
    } else if (event.type === 'error') {
      onError(event.message)
    }
  }
}

// AI 扩写【新】：仅依据核心信息，输出结构与旧版一致（复用 AINodeResponse）
export async function expandAINodeCoreStream(
  request: AICoreNodeRequest,
  onChunk: (text: string) => void,
  onResult: (data: AINodeResponse) => void,
  onError: (msg: string) => void
): Promise<void> {
  const response = await serviceNW.post('/api/ai_node/core/stream', request, {
    responseType: 'text',
    transformResponse: [(data: string) => data],
  })

  const lines = (response.data as string).split('\n').filter((l: string) => l.trim())
  for (const line of lines) {
    const event: StreamEvent = JSON.parse(line)
    if (event.type === 'chunk') {
      onChunk(event.content)
    } else if (event.type === 'result') {
      onResult(event.data as AINodeResponse)
    } else if (event.type === 'retry') {
      onChunk('\n\n' + event.message + '\n\n')
    } else if (event.type === 'error') {
      onError(event.message)
    }
  }
}

// ===== 节点内容局部调整 =====

export type RefineSectionType =
  | 'image_prompt'
  | 'background'
  | 'character_status'
  | 'prologue'
  | 'current_plot'
  | 'ending_example'
  | 'user_message_trigger'
  | 'user_message_ai_reply'
  | 'user_message_example'
  | 'easter_egg_name'
  | 'easter_egg_trigger'

interface RefineSectionRequest {
  // 调整目标
  section: RefineSectionType
  user_suggestion: string
  branch_index?: number | null
  indicator_index?: number | null
  egg_index?: number | null

  // 当前节点完整数据
  image_prompt?: string
  background?: string
  prologue?: PrologueItem[]
  character_status?: CharacterStatus[]
  task?: string
  user_messages?: UserMessageBranch[]
  easter_eggs?: EasterEgg[]
  progress_indicators?: ProgressIndicator[]

  // 原始剧本信息
  game_id?: number
  plot_start?: string
  plot_process?: string
  plot_endings?: PlotEnding[]
  target_node_id?: number
  previous_nodes?: any[]
  user_role?: string
  roles?: RoleInfo[]
  scene_roles?: string[]

  model?: string
}

export interface RefineSectionResponse {
  section: RefineSectionType
  refined_content: string
  raw_output?: string
}

export async function refineSectionStream(
  request: RefineSectionRequest,
  onChunk: (text: string) => void,
  onResult: (data: RefineSectionResponse) => void,
  onError: (msg: string) => void
): Promise<void> {
  const response = await serviceNW.post('/api/ai_node/refine/stream', request, {
    responseType: 'text',
    transformResponse: [(data: string) => data],
  })

  const lines = (response.data as string).split('\n').filter((l: string) => l.trim())
  for (const line of lines) {
    const event: StreamEvent = JSON.parse(line)
    if (event.type === 'chunk') {
      onChunk(event.content)
    } else if (event.type === 'result') {
      onResult(event.data as RefineSectionResponse)
    } else if (event.type === 'error') {
      onError(event.message)
    }
  }
}

export async function refineSection(
  request: RefineSectionRequest
): Promise<RefineSectionResponse> {
  const response = await serviceNW.post('/api/ai_node/refine', request)
  return response.data
}

// ===== 故事背景 =====

export interface GenerateBackgroundRequest {
  game_id: number
  target_node_id: number
  roles?: { role_id: number; role_name: string; description?: string }[]
  involved_roles?: { name: string; description?: string }[]
  model?: string
}

export interface GenerateBackgroundResponse {
  background: string
  previous_node_count: number // 0 表示这是第一个节点，背景为空是正常的
}

// 单独生成一个节点的故事背景（前序节点 → LLM 摘要）。
//
// 【程序扩写】用：那条路径零 AI，而核心信息里本来就不写背景，
// 所以背景只能由这个专门的接口生成。服务端与 AI 扩写【新】共用同一实现，
// 两条路径不会给出不一样的背景。
//
// 路径挂在 /api/ai_node/core/ 下面是有原因的：线上 nginx 只把
// /api/fixed_node/stream、/api/ai_node/stream、/api/ai_node/core/ 三条转发给
// novelweaver，其余 /api/* 都落到 Python 后端。新接口另起路径的话，
// 两边都部署成功了却仍然 404。加新接口时照这个前缀放。
export async function generateBackground(
  request: GenerateBackgroundRequest
): Promise<GenerateBackgroundResponse> {
  const response = await serviceNW.post('/api/ai_node/core/background', request)
  return response.data
}
